import React, { useState, useEffect } from "react";
import { Radio, Row, Col, Switch, Checkbox } from "antd";
import {
	ProductPricingPlan,
	Distributor,
	distributorLabel
} from "@/Contracts/Products";
import { RadioChangeEvent } from "antd/lib/radio";

import { Form } from "@ant-design/compatible";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";

import ZTypography from "@atoms/ZTypography";
import ZAlert from "@atoms/ZAlert";
import ZIcon from "@atoms/ZIcon";
import ZTooltip from "@atoms/ZTooltip";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { DynamicPricingOptionsProps, PricingRadioProps } from "./interface";
import DistributorPricingOption from "@molecules/DistributorPricingOption";
import SellPriceRoundingOptions from "../SellPriceRoundingOptions";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { currencyFormatter } from "@/Utils/Currency";
import amazonPrimeLogo from "config/assets/Logos/Prime.png";
import ZImage from "@atoms/ZImage";
import { ZCheckboxGroup } from "@atoms";
import { DocumentStatus } from "@/Contracts/Documents";

const { ZText } = ZTypography;

export const DynamicPricingOptions: React.FC<DynamicPricingOptionsProps> = ({
	pricing,
	selectedPricingId,
	selectedWarehouse,
	handlePricingIdChange,
	handleWarehouseChange,
	hideWarehouseOptions,
	formAlignment,
	isAutoUpdatePricingEnabled,
	isAutoUpdateSwitchEnabled,
	handleAutoUpdatePricingChange,
	handleSelectedDistributorsChange,
	selectedDistributors,
	handlePricingsChange,
	roundingDetails,
	handleRoundingDetailsChange,
	calculatedSellPrice,
	isReadOnly = false,
	isAmazonProduct = false,
	costPrice,
	currentActiveDocument,
	isProductPageAndAmazonCatalog = false
}) => {
	const [pricingVsWarehouseArr, setPricingVsWarehouseArr] = useState<
		{
			pricing: string | null;
			warehouse: string | undefined;
			vendor_name: string | undefined;
		}[]
	>([]);
	const [selectedProductPricingOptionId, setSelectedProductPricingOptionId] =
		useState<string | null>(selectedPricingId ?? null);
	const shouldSkipRendering =
		(currentActiveDocument?.status === DocumentStatus.Won ||
			currentActiveDocument?.status === DocumentStatus.Lost) &&
		selectedPricingId &&
		!pricing?.find(p => p.id === selectedPricingId);

	useEffect(() => {
		setSelectedProductPricingOptionId(selectedPricingId ?? "");
	}, [pricing, selectedPricingId]);

	const onWarehouseChange = (
		currentPricing: ProductPricingPlan,
		newWarehouse: string | undefined
	) => {
		setPricingVsWarehouseArr(
			[...pricingVsWarehouseArr]
				.filter(element => element.pricing !== currentPricing.id)
				.concat({
					pricing: currentPricing.id,
					warehouse: newWarehouse,
					vendor_name: currentPricing.vendor_name
				})
		);
		if (isAutoUpdatePricingEnabled) {
			handleWarehouseChange?.(newWarehouse, currentPricing?.vendor_name);
			return;
		}
		handleWarehouseChange?.(newWarehouse);
	};

	const onPricingIdChange = (pricingId: string) => {
		setSelectedProductPricingOptionId(pricingId);
		const selectedPricing = pricing?.find(
			pricing => pricing.id === pricingId
		);
		const warehouse = pricingVsWarehouseArr.find(
			element => element.pricing === selectedPricing?.id
		)?.warehouse;
		handleWarehouseChange?.(warehouse);

		handlePricingIdChange({ value: pricingId, radioChangeEvent: null });

		if (!!selectedPricing?.distributor_pricing_details) {
			handleSelectedDistributorsChange([
				{
					vendor_name:
						selectedPricing.distributor_pricing_details.vendor_name,
					distributor:
						selectedPricing.distributor_pricing_details.distributor,
					selected_warehouse: warehouse ?? null
				}
			]);
		} else {
			handleSelectedDistributorsChange(null);
			handleAutoUpdatePricingChange(false);
		}
	};

	const onAutoUpdatePriceChange = (value: boolean) => {
		resetOptions();
		handleAutoUpdatePricingChange(value);
		if (value) {
			const distributors = pricing?.reduce(
				(
					distributors: {
						distributor: Distributor;
						selected_warehouse: string | null;
						costPrice: number;
						pricingId: string;
						vendor_name?: string;
					}[],
					distributorPricing
				) => {
					const distributorName =
						distributorPricing.distributor_pricing_details
							?.distributor;
					const selectOptions =
						(pricing &&
							distributorPricing.distributor_pricing_details?.branch_wise_details?.map(
								warehouse => ({
									name: `${warehouse.branch_name} (${warehouse.availability}/${warehouse.on_order})`,
									value: warehouse.branch_name
								})
							)) ??
						[];
					const warehouse =
						distributorPricing.distributor_pricing_details
							?.distributor &&
						!!selectedDistributors?.length &&
						[
							...selectedDistributors.map(
								distributor => distributor.distributor
							)
						].includes(
							distributorPricing.distributor_pricing_details
								?.distributor
						)
							? selectedWarehouse
							: selectOptions?.[0]?.value;

					const costPrice = distributorPricing.cost_price;
					const pricingId = distributorPricing.id ?? "";
					if (distributorName && costPrice) {
						distributors.push({
							distributor: distributorName,
							selected_warehouse: warehouse ?? null,
							costPrice,
							pricingId,
							vendor_name: distributorPricing.vendor_name
						});
					}
					return distributors;
				},
				[]
			);
			distributors && handleSelectedDistributorsChange(distributors);
			const lowestPricedDistributor = !!distributors?.length
				? distributors?.reduce((acc, distributor) =>
						acc?.costPrice < distributor?.costPrice
							? acc
							: distributor
				  )
				: {
						distributor: Distributor.Ingram,
						selected_warehouse: null,
						costPrice: 0,
						pricingId: ""
				  };
			if (!!lowestPricedDistributor?.pricingId?.length) {
				onPricingIdChange(lowestPricedDistributor?.pricingId);
			}
		} else {
			const lowestPricedDistributor = pricing?.reduce(
				(
					distributor: {
						name: string | null;
						price: number | null;
						pricingId: string | null;
						vendor_name?: string;
					},
					distriburtorPricing
				) => {
					if (distributor?.price) {
						if (
							distriburtorPricing.distributor_pricing_details &&
							distributor.price >
								distriburtorPricing.distributor_pricing_details
									?.sell_price
						) {
							distributor.name =
								distriburtorPricing.distributor_pricing_details.distributor;
							distributor.price =
								distriburtorPricing.distributor_pricing_details.sell_price;
							distributor.pricingId = distriburtorPricing.id;
							distributor.vendor_name =
								distriburtorPricing.distributor_pricing_details.vendor_name;
						}
					} else {
						distributor.name =
							distriburtorPricing?.distributor_pricing_details
								?.distributor ?? null;
						distributor.price =
							distriburtorPricing?.distributor_pricing_details
								?.sell_price ?? null;
						distributor.pricingId = distriburtorPricing?.id ?? null;
						distributor.vendor_name =
							distriburtorPricing?.distributor_pricing_details?.vendor_name;
					}

					return distributor;
				},
				{ name: null, price: null, pricingId: null }
			);

			const warehouse = pricingVsWarehouseArr.find(
				element => element.pricing === selectedPricingId
			)?.warehouse;

			const distributor = {
				vendor_name: lowestPricedDistributor?.vendor_name,
				distributor: lowestPricedDistributor?.name as Distributor,
				selected_warehouse: warehouse ?? null
			};
			lowestPricedDistributor?.name &&
				handleSelectedDistributorsChange([distributor]);
			onPricingIdChange(lowestPricedDistributor?.pricingId ?? "");
		}
	};

	const getPricingVsWarehouseArr = () => {
		const pricingVsWarehouse = isAutoUpdatePricingEnabled
			? selectedDistributors?.map(distributor => ({
					pricing:
						pricing?.find(
							pricing =>
								!!pricing.distributor_pricing_details &&
								pricing.vendor_name === distributor.distributor
						)?.id ?? "",
					warehouse:
						distributor.selected_warehouse ??
						pricing?.find(
							pricing =>
								!!pricing.distributor_pricing_details &&
								pricing.vendor_name === distributor.distributor
						)?.distributor_pricing_details?.branch_wise_details?.[0]
							?.branch_name,
					vendor_name: distributor.distributor
			  }))
			: pricing?.map(pricing => {
					const warehouse =
						pricing.distributor_pricing_details?.branch_wise_details.find(
							branch => branch.branch_name === selectedWarehouse
						)?.branch_name ||
						pricing.distributor_pricing_details
							?.branch_wise_details[0]?.branch_name;
					return {
						pricing: pricing.id,
						warehouse: warehouse,
						vendor_name: pricing.vendor_name
					};
			  });
		setPricingVsWarehouseArr(
			!!pricingVsWarehouse?.length ? pricingVsWarehouse : []
		);
	};

	useEffect(() => {
		getPricingVsWarehouseArr();
	}, [pricing, selectedWarehouse]);

	const isSellPriceAutoRoundOffEnabled = useFeatureFlag(
		"sellprice_auto_roundoff"
	);

	const catalogPricing =
		(isAmazonProduct
			? pricing
			: pricing?.filter(
					pricing => !!!pricing.distributor_pricing_details
			  )) ?? [];

	const distributorPricing =
		(isAmazonProduct
			? pricing?.filter(
					pricing => !!pricing.amazon_business_pricing_details
			  )
			: pricing?.filter(
					pricing => !!pricing.distributor_pricing_details
			  )) ?? [];

	const handleDistributorChange = (value: CheckboxValueType[]) => {
		const distributors = value.map(value => {
			const warehouse = pricingVsWarehouseArr.find(
				warehouse => warehouse?.pricing === (value as string)
			)?.warehouse;
			const distributor_pricing_details = pricing?.find(pricingPlan => {
				return pricingPlan.id === value;
			})?.distributor_pricing_details;

			const distributorDetail: {
				distributor: Distributor;
				selected_warehouse: string | null;
				vendor_name?: string;
			} = {
				distributor:
					distributor_pricing_details?.distributor as Distributor,
				selected_warehouse: !!warehouse?.length
					? warehouse
					: distributor_pricing_details?.branch_wise_details?.[0]
							?.branch_name ?? null
			};
			if (
				distributor_pricing_details?.distributor ===
				Distributor.CustomDistributor
			) {
				distributorDetail.vendor_name =
					distributor_pricing_details.vendor_name;
			}
			return distributorDetail;
		});
		handleSelectedDistributorsChange(distributors);
	};

	const resetOptions = () => {
		setSelectedProductPricingOptionId(null);
		handleSelectedDistributorsChange(null);
	};

	const selectOptions =
		selectedDistributors?.map(distributor => {
			return (
				pricing?.find(pricingData => {
					if (
						distributor.distributor ===
						Distributor.CustomDistributor
					) {
						return (
							pricingData.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_distributor.id ===
							distributor.selected_custom_distributor_details
								?.custom_distributor_id
						);
					}
					return (
						pricingData.distributor_pricing_details?.distributor ===
						distributor.distributor
					);
				})?.id || ""
			);
		}) || [];

	if (!shouldSkipRendering) {
		const radioGroup = (
			<Radio.Group
				className="product-image-radio-container w-full"
				onChange={(e: RadioChangeEvent) =>
					onPricingIdChange(e.target.value)
				}
				value={selectedProductPricingOptionId}
				disabled={isReadOnly}
			>
				{!!catalogPricing.length && (
					<Col span={24} className="text-1 pb-4">
						My catalog
					</Col>
				)}
				{catalogPricing.map(pricing => {
					return (
						<PricingRadio
							pricing={pricing}
							key={pricing.id}
							isReadOnly={isReadOnly}
							isAmazonProduct={isAmazonProduct}
						/>
					);
				})}

				<>
					<div className="pb-4 mt-6">
						<ZText>Realtime pricing</ZText>
					</div>
					<div className="bg-gray-2 text-gray-8 p-4">
						<div className="flex items-center">
							<ZText className="pr-2 text-gray-8">
								Enable Auto Update
							</ZText>
							<ZTooltip
								title="Auto update pricing  is not available for Amazon distributors."
								showtooltip={isAmazonProduct}
								placement="top"
							>
								<Switch
									size="small"
									checked={isAutoUpdatePricingEnabled}
									onChange={onAutoUpdatePriceChange}
									data-testid="auto-update-pricing"
									disabled={
										!isAutoUpdateSwitchEnabled ||
										isReadOnly ||
										isAmazonProduct
									}
									className="my-0"
								/>
							</ZTooltip>
							{!isAmazonProduct && (
								<div className="flex-shrink ml-2">
									<ZTooltip
										title="If Auto Update is enabled, the cost price will be fetched from the selected distributors, and sell price will be calculated using the margin/markup."
										showtooltip={true}
									>
										<ZIcon
											name={EIcons.INFOCIRCLE}
											color={EColors.GRAY}
											colorVariant={6}
											size="sm"
										/>
									</ZTooltip>
								</div>
							)}
							{isSellPriceAutoRoundOffEnabled &&
								!isReadOnly &&
								isAutoUpdatePricingEnabled && (
									<SellPriceRoundingOptions
										calculatedSellPrice={
											calculatedSellPrice
										}
										roundingDetails={roundingDetails}
										handleRoundingDetailsChange={
											handleRoundingDetailsChange
										}
									/>
								)}
						</div>

						{isAutoUpdatePricingEnabled && (
							<Row className="pt-2">
								<ZAlert
									type="info"
									showIcon
									message={
										<ZText className="text-gray-8">
											Lowest price among the selected
											distributors will be selected
											automatically.
										</ZText>
									}
								/>
							</Row>
						)}

						<Row className="pb-2 pt-4">
							<Col span={12}>
								<ZText className="text-gray-8">
									Distributors
								</ZText>
							</Col>
							<Col span={12}>
								<ZText className="text-gray-8">Warehouse</ZText>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								{!isAmazonProduct &&
									(isAutoUpdatePricingEnabled ? (
										<ZCheckboxGroup
											onChange={handleDistributorChange}
											className="w-full"
											value={selectOptions}
											disabled={isReadOnly}
										>
											{distributorPricing.map(pricing => {
												const warehouse =
													pricingVsWarehouseArr.find(
														element =>
															element.pricing ===
															pricing.id
													)?.warehouse;
												return (
													<DistributorPricingOption
														handlePricingsChange={
															handlePricingsChange
														}
														pricing={pricing}
														key={pricing.id}
														selectedWarehouse={
															warehouse
														}
														onWarehouseChange={
															onWarehouseChange
														}
														hideWarehouseOptions={
															hideWarehouseOptions
														}
														isAutoUpdatePricingEnabled={
															isAutoUpdatePricingEnabled
														}
														selectedDistributors={
															selectedDistributors ??
															[]
														}
														isReadOnly={isReadOnly}
													/>
												);
											})}
										</ZCheckboxGroup>
									) : (
										<>
											{distributorPricing.map(pricing => {
												const warehouse =
													pricingVsWarehouseArr.find(
														element =>
															element.pricing ===
															pricing.id
													)?.warehouse;
												return (
													<DistributorPricingOption
														pricing={pricing}
														key={pricing.id}
														selectedWarehouse={
															warehouse
														}
														onWarehouseChange={
															onWarehouseChange
														}
														hideWarehouseOptions={
															hideWarehouseOptions
														}
														isAutoUpdatePricingEnabled={
															isAutoUpdatePricingEnabled
														}
														selectedDistributors={
															selectedDistributors ??
															[]
														}
														isReadOnly={isReadOnly}
													/>
												);
											})}
										</>
									))}
								<ShowDistributorNotAvailableRadio
									pricing={pricing}
									isAutoUpdatePricingEnabled={
										isAutoUpdatePricingEnabled
									}
									isAmazonProduct={isAmazonProduct}
									selectedProductPricingOptionId={
										selectedProductPricingOptionId
									}
									costPrice={costPrice}
								/>
							</Col>
						</Row>
					</div>
				</>

				{!isAmazonProduct && (
					<>
						{" "}
						<Col span={24} className="text-1 py-4 mt-6">
							Create new
						</Col>
						<Radio
							className="product-image-radio"
							checked={!selectedPricingId}
							data-testid="test-pricing-id-custom"
							value={""}
							disabled={isReadOnly}
						>
							Custom
						</Radio>
					</>
				)}
			</Radio.Group>
		);
		if (isProductPageAndAmazonCatalog) {
			return radioGroup;
		}
		return (
			<Form.Item {...formAlignment} label="Pricing" labelAlign="left">
				{radioGroup}
			</Form.Item>
		);
	}

	return <></>;
};

const PricingRadio = ({
	pricing,
	isReadOnly = false,
	isAmazonProduct = false
}: PricingRadioProps) => {
	const amazonBusinessPricingDetail = pricing.amazon_business_pricing_details;
	const amazonPricingVATlabel =
		amazonBusinessPricingDetail?.tax_exclusive_price.label;
	const pricingCatalogLabel = isAmazonProduct ? (
		<ZText>
			{pricing.name ?? ""} {!!amazonBusinessPricingDetail && ":"}&nbsp;
			{!!amazonBusinessPricingDetail && (
				<ZText weight={EFontWeights.MEDIUM} level={0}>
					&nbsp;
					{currencyFormatter(
						amazonBusinessPricingDetail.tax_exclusive_price.price ??
							amazonBusinessPricingDetail?.cost_price ??
							""
					)}
				</ZText>
			)}
			{amazonPricingVATlabel && (
				<ZText weight={EFontWeights.MEDIUM} level={0}>
					&nbsp;{amazonPricingVATlabel}
				</ZText>
			)}
			{amazonBusinessPricingDetail?.product_condition && (
				<ZText level={0}>
					&nbsp;({amazonBusinessPricingDetail.product_condition}),
				</ZText>
			)}
			<ZText level={0}>
				&nbsp;
				{amazonBusinessPricingDetail?.delivery_information}
			</ZText>
			{amazonBusinessPricingDetail?.shipping_cost && (
				<ZText level={0}>
					,&nbsp; Additional shipping cost:{" "}
					{currencyFormatter(
						amazonBusinessPricingDetail?.shipping_cost[0]
					)}
				</ZText>
			)}
			{amazonBusinessPricingDetail?.is_prime && (
				<ZImage
					width={46}
					height={13}
					preview={false}
					alt="Prime Tag"
					className={`mt-0.5 ml-2`}
					src={amazonPrimeLogo}
				/>
			)}
		</ZText>
	) : (
		pricing.name ?? "Default Pricing"
	);
	return (
		<Col span={24}>
			<Radio value={pricing.id} className="mb-2" disabled={isReadOnly}>
				{pricingCatalogLabel}
			</Radio>
		</Col>
	);
};

const UnavailableInfo = () => {
	return (
		<Row>
			<Col span={23}>
				<ZText className="text-gray-8">
					Stock Level: Temporarily Unavailable
				</ZText>
			</Col>
			<Col span={1}>
				<ZTooltip
					title="This distributor's APIs are temporarily down for maintenance."
					showtooltip={true}
					className="ml-1 mt-1"
				>
					<ZIcon
						name={EIcons.INFOCIRCLE}
						color={EColors.GRAY}
						colorVariant={6}
						size="sm"
					/>
				</ZTooltip>
			</Col>
		</Row>
	);
};

export const ShowDistributorNotAvailableRadio = (props: {
	pricing: ProductPricingPlan[] | null;
	isAutoUpdatePricingEnabled: boolean;
	isAmazonProduct: boolean;
	selectedProductPricingOptionId: string | null;
	costPrice?: string | number;
}) => {
	const integrations = useSelector(
		(state: GlobalState) => state.user.userCompany.integrations
	);
	const isIngramIntegrated = integrations.ingram !== null;
	const isTechdataIntegrated = integrations.tech_data !== null;
	const isDAndHIntegrated = integrations.d_and_h !== null;
	const isSynnexIntegrated = integrations.synnex !== null;
	const isAmazonIntegrated = integrations.amazon_business !== null;

	const showIngram =
		isIngramIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.Ingram
		);

	const showTechData =
		isTechdataIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.TechData
		);

	const showDAndH =
		isDAndHIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.DAndH
		);

	const showSynnex =
		isSynnexIntegrated &&
		!!!props.pricing?.find(
			pricing =>
				pricing.distributor_pricing_details?.distributor ===
				Distributor.Synnex
		);
	const showAmazon = isAmazonIntegrated;
	const showDistributor =
		showIngram || showTechData || showDAndH || showSynnex || showAmazon;
	const radioOrCheckbox = (
		distributor: Distributor,
		pricing: ProductPricingPlan[] | null
	) => {
		const vendorName = pricing?.find(
			pricing => pricing.vendor_name === distributor
		);

		const selectedPricingIndex = pricing?.findIndex(
			item => item.id === props.selectedProductPricingOptionId
		);
		const amazonDefaultPricing =
			(selectedPricingIndex ?? 0) < 0
				? props.costPrice
				: pricing?.[selectedPricingIndex ?? 0]?.cost_price ?? 0;

		const NaInformation = () => {
			return (
				<>
					{!!vendorName?.vendor_sku_no ? (
						<UnavailableInfo />
					) : (
						<ZText className="text-gray-8">Stock Level: NA</ZText>
					)}
				</>
			);
		};
		return !props.isAutoUpdatePricingEnabled ? (
			<Row className="product-distributor-control py-2">
				<Col span={12}>
					<Radio
						className="product-image-radio"
						value={distributor.toLocaleLowerCase()}
						disabled
					>
						<ZText
							weight={EFontWeights.MEDIUM}
							className="text-gray-8"
						>
							{distributorLabel[distributor]}
						</ZText>
						{" | "}
						{props.isAmazonProduct &&
						distributor === Distributor.Amazon ? (
							<ZText>
								{currencyFormatter(amazonDefaultPricing)}
							</ZText>
						) : (
							<ZText className="text-gray-8">Not available</ZText>
						)}
						<br />
						<NaInformation />
					</Radio>
				</Col>
			</Row>
		) : (
			<Row className="product-distributor-control py-2">
				<Col span={12}>
					<Checkbox
						className="product-image-radio"
						value={"ingram"}
						disabled
					>
						<ZText
							weight={EFontWeights.MEDIUM}
							className="text-gray-8"
						>
							{distributorLabel[distributor]}
						</ZText>
						{" | "}
						<ZText className="text-gray-8">Not available</ZText>
						<br />
						<NaInformation />
					</Checkbox>
				</Col>
			</Row>
		);
	};

	return showDistributor ? (
		<Radio.Group
			className="product-distributor-container w-full"
			value={
				props.isAmazonProduct && !!props.selectedProductPricingOptionId
					? Distributor.Amazon
					: ""
			}
			disabled
		>
			{showIngram && radioOrCheckbox(Distributor.Ingram, props.pricing)}
			{showTechData &&
				radioOrCheckbox(Distributor.TechData, props.pricing)}
			{showDAndH && radioOrCheckbox(Distributor.DAndH, props.pricing)}
			{showSynnex && radioOrCheckbox(Distributor.Synnex, props.pricing)}
			{showAmazon && radioOrCheckbox(Distributor.Amazon, props.pricing)}
		</Radio.Group>
	) : (
		<></>
	);
};

export default DynamicPricingOptions;
